import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Snackbar,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import MuiAlert from "@mui/material/Alert";
import {
  getFeeSchedulesForHospitalServicesManagementById,
  updateFeeSchedulesForHospitalServicesManagement,
} from "./../../ApiConfig.jsx";

const validationSchema = yup.object().shape({
  services: yup.string().required("Service is required"),
  typeOfService: yup.string(),
  hospitalname: yup.string().required("Hospital name is required"),
  isCovered: yup.string().required("Covered is required"),
  fee: yup
    .string()
    .required("Fee is required")
    .matches(/^\d+$/, "Fee must contain only numbers"),
});

function EditFeeSchedulesForHospitalServices() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [status, setStatus] = useState("Inactive");
  const initialUserDetails = {
    services: "",
    typeOfService: "",
    hospitalname: "",
    isCovered: "",
    fee: "",
  };

  const [itemDetails, setItemDetails] = useState(initialUserDetails);
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchItemData = async (id) => {
    try {
      const data = await getFeeSchedulesForHospitalServicesManagementById(id);
      if (data.status) {
        console.log(data.result);
        setStatus(data.result.status);
        setItemDetails({
          services: data.result.services,
          typeOfService: data.result.typeOfService,
          hospitalname: data.result.hospitalname,
          isCovered: data.result.isCovered,
          fee: data.result.fee,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchItemData(id);
  }, [id]);



  const validateField = (field, value) => {
    validationSchema
      .validateAt(field, { ...itemDetails, [field]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
      })
      .catch((error) => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: error.message }));
      });
  };

  const handleFieldChange = (field, value) => {
    setItemDetails((prevUserDetails) => ({
      ...prevUserDetails,
      [field]: value,
    }));
    validateField(field, value);
  };

  const updateHandler = async (userDetails) => {
    const hasErrors = Object.values(errors).some(
      (errorMessage) => errorMessage !== ""
    );

    if (hasErrors) {
      console.log("Errors found. Cannot update fee schedules for hospital.");
      return;
    }

    console.log(userDetails);
    setIsLoading(true);
    const body = {
      ...userDetails,
      status,
      isCovered:userDetails.isCovered==="true",
    };
    try {
      const respondData = await updateFeeSchedulesForHospitalServicesManagement(
        id,
        body
      );
      console.log(respondData);
      if (respondData.status) {
        setAlertSeverity("success");
        setAlertMessage("Fee schedule edited successfully");
        setTimeout(() => {
          navigate("/feeSchedulesForHospitalServicesManagement");
        }, 2500);
      }
    } catch (err) {
      console.log(err);
      setAlertSeverity("error");
      setAlertMessage(`Failed to edit fee schedule: ${err.message}`);
    } finally {
      setOpenSnackbar(true);
      setIsLoading(false);
    }
  };

  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Header
        title={`Edit Fee Schedules For Hospital Services ${id}`}
        subtitle=""
      />
      <Box ml={"40px"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Service*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.services}
              onChange={(e) => handleFieldChange("services", e.target.value)}
              error={!!errors.services}
              helperText={errors.services}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Type of service*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.typeOfService}
              onChange={(e) =>
                handleFieldChange("typeOfService", e.target.value)
              }
              error={!!errors.typeOfService}
              helperText={errors.typeOfService}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Hospital name*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.hospitalname}
              onChange={(e) =>
                handleFieldChange("hospitalname", e.target.value)
              }
              error={!!errors.hospitalname}
              helperText={errors.hospitalname}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Covered*
            </Typography>
            <FormControl fullWidth variant="filled">
              <Select
                value={itemDetails.isCovered}
                onChange={(e) => handleFieldChange("isCovered", e.target.value)}
                name="isCovered"
                error={!!errors.isCovered}>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
              {errors.isCovered && (
                <Typography variant="caption" color="error">
                  {errors.isCovered}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Fee*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.fee}
              onChange={(e) => handleFieldChange("fee", e.target.value)}
              error={!!errors.fee}
              helperText={errors.fee}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#6870fa",
                color: "white",
                fontSize: "16px",
                "&:hover": {
                  backgroundColor: "#3e4396",
                },
              }}
              onClick={() => updateHandler(itemDetails)}>
              {isLoading ? "Updating..." : "Update"}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <MuiAlert
          onClose={() => setOpenSnackbar(false)}
          severity={alertSeverity}
          elevation={6}
          variant="filled"
          style={{ color: "white" }}>
          {alertSeverity === "success" ? "Success" : "Error"}
          {": "}
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default EditFeeSchedulesForHospitalServices;
